import { EditableStep, ResultType, StepType } from '@digila-ui/types'
import { AuthorizationType, DigiLaFlow, MinorServiceType } from '@shared/digila'
import {
  AnalyticsContextValue,
  AnalyticsData,
  AnalyticsService,
  CommonAnalyticsCustomEventName,
  CommonAnalyticsModalName,
  NotAvailable,
  sendCustomDataEventToAnalytics,
  sendCustomEventToAnalytics,
  sendPageViewEventToAnalytics,
} from '@ui-common/analytics'
import { useSendPageViewEvent } from '@ui-common/hooks'
import { getFlow, getSourceChannel } from '@ui-common/utils/helpers'
import { getLanguage } from '@ui-common/utils/language'
import { createContext, use, useMemo } from 'react'
import * as R from 'remeda'

import { ServiceSelections, useServiceSelectionsContext } from '../state/ServiceSelectionContext'

type DigilaPageViewEventName =
  | 'Authenticate'
  | 'BankAccountDetails'
  | 'BankAccountBasics'
  | 'ChildAoMembership'
  | 'ChildKyc'
  | 'ChildSsn'
  | 'End'
  | 'Entrance'
  | 'GetUserInfo'
  | 'KycGate'
  | 'GuardianKyc'
  | 'Initialize'
  | 'InvestmentServicesDetails'
  | 'Authorization'
  | 'SecondGuardianInfo'
  | 'ServiceCatalog'
  | 'Status'
  | 'Summary'
  | 'Consent'
  | 'Sending'
  | 'Accepting'
  | 'Rejecting'
  | 'DigipaRequired'
  | 'DigiliRequired'

type ApplicationEndTypeEventName =
  | 'ResultApplicationInProgress'
  | 'ResultApplicationPendingGuardianApproval'
  | 'ResultGuardianKycInvalid'
  | 'ResultMinorKycInvalid'
  | 'ResultApplicationSent'
  | 'ResultApplicationRejectedByCoGuardian'

export type DigilaActionEventName = CommonAnalyticsCustomEventName | 'ResetApplicationButton'
type DigilaAnalyticsData = AnalyticsData

export enum DigilaModalEventName {
  SecondGuardianPhoneNumberModal = 'SecondGuardianPhoneNumberModal',
  NonTechnicalErrorModal = 'NonTechnicalErrorModal',
  UserIsNotMainAoMemberErrorModal = 'UserIsNotMainAoMemberErrorModal',
  RepresentativeRightsUnknownErrorModal = 'RepresentativeRightsUnknownErrorModal',
  GuardianMismatchErrorModal = 'GuardianMismatchErrorModal',
  GuardianResetApplicationModal = 'GuardianResetApplicationModal',
  SecondGuardianRejectModal = 'SecondGuardianRejectModal',
  NoProductsSelectedModal = 'NoProductsSelectedModal',
  NetbankPowerOfAttorneyNotAvailableModal = 'NetbankPowerOfAttorneyNotAvailableModal',
  MandatoryBankAccountForAuthorizationsModal = 'MandatoryBankAccountForAuthorizationsModal',
}

export const wizardStepToAnalyticsEvent: Record<StepType, DigilaPageViewEventName> = {
  [StepType.Authenticate]: 'Authenticate',
  [StepType.ServiceCatalog]: 'ServiceCatalog',
  [StepType.BankAccountDetails]: 'BankAccountDetails',
  [StepType.BankAccountBasics]: 'BankAccountBasics',
  [StepType.MinorAoMembership]: 'ChildAoMembership',
  [StepType.MinorKYC]: 'ChildKyc',
  [StepType.MinorSsn]: 'ChildSsn',
  [StepType.End]: 'End',
  [StepType.Entrance]: 'Entrance',
  [StepType.GetUserInfo]: 'GetUserInfo',
  [StepType.KycGate]: 'KycGate',
  [StepType.GuardianKyc]: 'GuardianKyc',
  [StepType.Initialize]: 'Initialize',
  [StepType.InvestmentServicesDetails]: 'InvestmentServicesDetails',
  [StepType.Authorization]: 'Authorization',
  [StepType.Status]: 'Status',
  [StepType.SendStep]: 'Sending',
  [StepType.AcceptStep]: 'Accepting',
  [StepType.RejectStep]: 'Rejecting',
  [StepType.DigipaRequiredGate]: 'DigipaRequired',
  [StepType.DigiliRequiredGate]: 'DigiliRequired',
  [StepType.SecondGuardianStep]: 'SecondGuardianInfo',
}

export type SendCustomEvent = (
  view: DigilaPageViewEventName | DigilaModalEventName | CommonAnalyticsCustomEventName,
  name: DigilaActionEventName | CommonAnalyticsCustomEventName | ApplicationEndTypeEventName,
) => void

export const applicationResultMap: Record<ResultType, ApplicationEndTypeEventName> = {
  [ResultType.ApplicationPending]: 'ResultApplicationInProgress',
  [ResultType.GuardianKycInvalid]: 'ResultGuardianKycInvalid',
  [ResultType.MinorKycInvalid]: 'ResultMinorKycInvalid',
  [ResultType.ApplicationWaitingForApproval]: 'ResultApplicationPendingGuardianApproval',
  [ResultType.ApplicationSent]: 'ResultApplicationSent',
  [ResultType.ApplicationRejected]: 'ResultApplicationRejectedByCoGuardian',
}

const DEFAULT_ANALYTICS_DATA: AnalyticsData = {
  src: NotAvailable,
  flow: NotAvailable,
  product: NotAvailable,
  version: NotAvailable,
  ui_lang: NotAvailable,
}

const productAnalyticsMap: Record<MinorServiceType | AuthorizationType, string> = {
  [MinorServiceType.OpenBankAccount]: 'OpenAccount',
  [MinorServiceType.OpenInvestmentService]: 'OpenInvestmentService',
  [AuthorizationType.PaymentCard]: 'CardPowerOfAttorney',
  [AuthorizationType.NetbankCredentials]: 'NetbankPowerOfAttorney',
}

export class DigilaAnalyticsService extends AnalyticsService<
  DigilaPageViewEventName,
  DigilaActionEventName,
  CommonAnalyticsModalName
> {
  constructor(private readonly data: DigilaAnalyticsData = DEFAULT_ANALYTICS_DATA) {
    super()
  }

  public updateProduct = (selectionContext: ServiceSelections): void => {
    if (!this.data?.product || this.data.product === NotAvailable) {
      let products = ''
      if (selectionContext?.selectedServices?.length > 0) {
        products = selectionContext.selectedServices.map((service) => productAnalyticsMap[service]).join(',')
      }

      let authorizationProducts = ''
      if (selectionContext.selectedAuthorizations?.length > 0) {
        authorizationProducts = selectionContext.selectedAuthorizations
          .map((authorization) => productAnalyticsMap[authorization])
          .join(',')

        products = `${products},${authorizationProducts}`
      }

      this.data.product = products || NotAvailable
    }
  }

  public getCommonData = (): AnalyticsData => this.data ?? DEFAULT_ANALYTICS_DATA

  public sendPageViewEvent = (step: DigilaPageViewEventName): void => {
    sendPageViewEventToAnalytics(step, this.getCommonData())
  }

  public sendCustomEvent: SendCustomEvent = (
    view: DigilaPageViewEventName | DigilaModalEventName | CommonAnalyticsCustomEventName,
    name: DigilaActionEventName | CommonAnalyticsCustomEventName | ApplicationEndTypeEventName,
  ): void => {
    sendCustomEventToAnalytics(view, name, this.getCommonData())
  }

  public sendPageCustomEvent = (step: StepType, name: DigilaActionEventName): void => {
    sendCustomEventToAnalytics(wizardStepToAnalyticsEvent[step], name, this.getCommonData())
  }

  public sendPageEditActionEvent = (step: EditableStep): void => {
    sendCustomEventToAnalytics(wizardStepToAnalyticsEvent[step], `${step}EditLink`, this.getCommonData())
  }

  public sendCustomDataEvent = (data: { [key: string]: string }): void => {
    sendCustomDataEventToAnalytics(data)
  }

  public sendModalViewEvent = (modal: DigilaModalEventName | CommonAnalyticsModalName): void => {
    sendPageViewEventToAnalytics(modal, this.getCommonData())
  }
}

type DigilaAnalyticsContextValue = AnalyticsContextValue<DigilaAnalyticsService>
export const DigilaAnalyticsContext: React.Context<DigilaAnalyticsContextValue> =
  createContext<DigilaAnalyticsContextValue>({
    analyticsService: new DigilaAnalyticsService(undefined),
  })

export const useDigilaAnalyticsContextValue = (): { analyticsService: DigilaAnalyticsService } => {
  const src = getSourceChannel() ?? NotAvailable
  const flow = getFlow() ?? DigiLaFlow.Default
  const language = getLanguage()

  return useMemo(
    () => ({
      analyticsService: new DigilaAnalyticsService({
        src,
        flow,
        product: NotAvailable,
        version: import.meta.env.VITE_APP_VERSION,
        ui_lang: language,
      }),
    }),
    [src, flow, language],
  )
}

export const useSendDigilaPageViewEvent = (step: StepType, isEditing?: boolean): void => {
  const { analyticsService } = use(DigilaAnalyticsContext)

  if (analyticsService === undefined) {
    throw new Error('useSendDigilaPageViewEvent must be used within a DigilaAnalyticsContext')
  }

  const pageEvent = wizardStepToAnalyticsEvent[step]
  useSendPageViewEvent(analyticsService, isEditing ? `${pageEvent}Edit` : pageEvent)
}

const analyticsServiceFunctions = [
  'sendCustomEvent',
  'sendCustomDataEvent',
  'sendPageCustomEvent',
  'sendPageEditActionEvent',
  'sendModalViewEvent',
] as const satisfies (keyof DigilaAnalyticsService)[]
type AnalyticsFunctionNames = (typeof analyticsServiceFunctions)[number]

export const useDigilaAnalyticsService = (): Pick<DigilaAnalyticsService, AnalyticsFunctionNames> => {
  const { analyticsService } = use(DigilaAnalyticsContext)

  const selectionsContext = useServiceSelectionsContext()
  analyticsService.updateProduct(selectionsContext)

  return R.pick(analyticsService, analyticsServiceFunctions)
}
